import { FC, ReactNode, useEffect, useRef } from 'react';
import { Button, Col, Flex, Row } from 'antd';
import { useTheme } from '@/contexts/themeContext';

interface StepperFormLayoutProps {
  children: React.ReactNode;
  handleSubmit: () => void;
  handleBackClick?: () => void;
  showBackButton: boolean;
  nextBtnText?: string;
  isSubmitLoading?: boolean;
  // New props for additional buttons
  showDraftButton?: boolean;
  handleSaveAsDraftClick?: () => void;
  isDraftLoading?: boolean;
  /** Extra content shown in the action bar above the buttons. */
  footerTop?: ReactNode;
}
const StepperFormLayout: FC<StepperFormLayoutProps> = ({
  children,
  handleSubmit,
  showBackButton,
  handleBackClick,
  nextBtnText = 'Next or Review',
  isSubmitLoading,
  handleSaveAsDraftClick,
  isDraftLoading,
  footerTop,
}) => {
  const { sidebarCollapseState } = useTheme();
  // Publish the action bar's height so the HubSpot chat bubble can sit just
  // above it (see _layout.scss), however tall the bar grows.
  const footerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const root = document.documentElement.style;
    const ro = new ResizeObserver(() =>
      root.setProperty('--stepper-footer-h', `${el.offsetHeight}px`),
    );
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.removeProperty('--stepper-footer-h');
    };
  }, []);
  return (
    <div
      style={{
        position: 'relative',
        // maxWidth: '1200px',
        margin: '0 auto',
        paddingBottom: '40px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* <div className="form-steps">steps</div> */}
      <div
        className="form-body"
        // A taller bar (footerTop) needs matching room so it never covers the
        // end of the page: bar height - 16px + the wrapper's 40px padding
        // leaves ~24px clear. Other pages keep the original spacing.
        style={{
          paddingBottom: footerTop
            ? 'calc(var(--stepper-footer-h, 74px) - 16px)'
            : '3.5rem',
        }}
      >
        {children}
      </div>
      <div
        ref={footerRef}
        className={`stepper-form-footer ${sidebarCollapseState ? 'sidebarContainerCollapse' : ''}`}
      >
        {footerTop}
        <Row>
          <Col xs={24}>
            <Flex
              justify={!showBackButton ? 'flex-end' : 'space-between'}
              gap={30}
            >
              {showBackButton && (
                <Button
                  size="large"
                  onClick={handleBackClick}
                  style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '14px 24px',
                  }}
                >
                  Back
                </Button>
              )}

              {/* Right side: Group draft and submit buttons together */}
              <Flex gap={30}>
                {handleSaveAsDraftClick && (
                  <Button
                    size="large"
                    onClick={handleSaveAsDraftClick}
                    loading={isDraftLoading}
                    disabled={isDraftLoading}
                    style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      padding: '14px 24px',
                    }}
                  >
                    Save As Draft
                  </Button>
                )}

                <Button
                  type="primary"
                  size="large"
                  onClick={handleSubmit}
                  loading={isSubmitLoading}
                  style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '14px 24px',
                  }}
                >
                  {nextBtnText}
                </Button>
              </Flex>
            </Flex>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default StepperFormLayout;
